import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useSiteStore } from '@core/site/useSiteStore';
import { useClientStore } from '@core/client/useClientStore';
import { SiteStatus, UserRole } from '@core/generated/gql-types';
import { userActions } from '@core/user/user.action';
import { generateFriendlyAddress } from '@core/util/google-places.service';
import {
  getUserDisplayName,
  SiteAssignmentOption,
} from '@/hooks/useSiteAssignment';
import Button from '@/components/Button';

interface ClientRolesAndPermissionsDetailsProps {
  row: SiteAssignmentOption;
  clientId: string;
  onBack: () => void;
}

const formatRoleLabel = (role: UserRole): string => {
  switch (role) {
    case UserRole.ClientAdmin:
      return 'Client Admin';
    case UserRole.ClientUser:
      return 'Client User';
    case UserRole.ClientViewer:
      return 'Client Viewer';
    default:
      return role;
  }
};

const ClientRolesAndPermissionsDetails: React.FC<
  ClientRolesAndPermissionsDetailsProps
> = ({ row, clientId, onBack }) => {
  const { getSitesForClient, setSitesForAccount } = useSiteStore();
  const { fetchUsersByClient, fetchInvitesForClient } = useClientStore();

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [firstNameValue, setFirstNameValue] = useState('');
  const [lastNameValue, setLastNameValue] = useState('');
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);

  const clientSites = useMemo(
    () =>
      getSitesForClient(clientId).sort((firstSite, secondSite) =>
        firstSite.name.localeCompare(secondSite.name),
      ),
    [getSitesForClient, clientId],
  );

  const isInvite = row.type === 'invite';

  const name = isInvite ? null : getUserDisplayName(row.user);
  const email = row.email;
  const role = isInvite
    ? row.invite.userRole
    : (row.user.role ?? UserRole.Unknown);
  const isClientAdmin = role === UserRole.ClientAdmin;
  const siteIds = isInvite ? row.invite.siteIds : row.user.siteIds;

  const canEditName = !isInvite; // Invites don't have a name yet, they have not signed up.
  const canEditSites = role === UserRole.ClientUser; // Client Users can edit their sites. Client Admins get full site access by default.
  const canEdit = canEditName || canEditSites; // Only allow editing if either name or sites can be edited.

  const accessibleSites = useMemo(() => {
    if (isClientAdmin) {
      return clientSites;
    }
    const idSet = new Set(siteIds ?? []);
    return clientSites.filter((site) => idSet.has(site.id));
  }, [isClientAdmin, clientSites, siteIds]);

  const startEditing = () => {
    setError(null);
    setFirstNameValue(isInvite ? '' : (row.user.firstName ?? ''));
    setLastNameValue(isInvite ? '' : (row.user.lastName ?? ''));
    setSelectedSiteIds(siteIds ?? []);
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (isSaving) return;
    setError(null);
    setIsEditing(false);
  };

  const toggleSite = (siteId: string) => {
    setSelectedSiteIds((currentIds) =>
      currentIds.includes(siteId)
        ? currentIds.filter((selectedId) => selectedId !== siteId)
        : [...currentIds, siteId],
    );
  };

  const selectAllSites = () =>
    setSelectedSiteIds(clientSites.map((site) => site.id));

  const clearSites = () => setSelectedSiteIds([]);

  const handleSave = async () => {
    setError(null);

    if (canEditName && firstNameValue.trim().length === 0) {
      setError('First name is required');
      return;
    }

    if (canEditName && lastNameValue.trim().length === 0) {
      setError('Last name is required');
      return;
    }

    if (canEditSites && selectedSiteIds.length === 0) {
      setError('At least one site is required');
      return;
    }

    setIsSaving(true);
    try {
      if (canEditName && row.type === 'user') {
        await userActions.updateUser(row.user.id, {
          firstName: firstNameValue.trim(),
          lastName: lastNameValue.trim(),
        });
      }

      if (canEditSites) {
        const result = await setSitesForAccount(
          isInvite
            ? { inviteId: row.invite.id, siteIds: selectedSiteIds }
            : { clientUserId: row.user.id, siteIds: selectedSiteIds },
        );

        if (!result.success) {
          setError(result.error ?? 'Failed to update sites');
          return;
        }
      }

      // Refetch so the list and this detail view reflect the saved changes.
      await Promise.all([
        fetchUsersByClient(clientId),
        fetchInvitesForClient(clientId),
      ]);

      setIsEditing(false);
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : 'An unexpected error occurred',
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-gray-800">
            {name ?? email}
          </h2>
          {isInvite && (
            <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
              Invited
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          {!isEditing && canEdit && (
            <Button
              type="button"
              variant="text"
              onClick={startEditing}
              className="gap-2 px-0 py-0 text-sm font-medium"
            >
              <FontAwesomeIcon icon={faPenToSquare} className="h-4 w-4" />
              Edit
            </Button>
          )}
          <Button
            type="button"
            variant="text"
            onClick={onBack}
            className="px-0 py-0 text-gray-400 hover:text-gray-600"
          >
            <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {isEditing ? (
        <div className="space-y-6 p-6">
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600">
              {error}
            </div>
          )}

          {canEditName && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label
                  className="mb-1 block text-sm font-medium text-gray-700"
                  htmlFor="account-first-name"
                >
                  First Name
                </label>
                <input
                  type="text"
                  id="account-first-name"
                  value={firstNameValue}
                  onChange={(event) => setFirstNameValue(event.target.value)}
                  className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label
                  className="mb-1 block text-sm font-medium text-gray-700"
                  htmlFor="account-last-name"
                >
                  Last Name
                </label>
                <input
                  type="text"
                  id="account-last-name"
                  value={lastNameValue}
                  onChange={(event) => setLastNameValue(event.target.value)}
                  className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Email Address
            </label>
            <input
              type="text"
              value={email}
              disabled
              className="w-full cursor-not-allowed rounded-md border border-gray-200 bg-gray-50 px-4 py-2 text-gray-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Role
            </label>
            <input
              type="text"
              value={formatRoleLabel(role)}
              disabled
              className="w-full cursor-not-allowed rounded-md border border-gray-200 bg-gray-50 px-4 py-2 text-gray-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Sites
            </label>

            {!canEditSites ? (
              <p className="text-sm text-gray-500">
                {isClientAdmin
                  ? 'Client Admins have access to every site.'
                  : 'Sites cannot be managed for this account.'}
              </p>
            ) : clientSites.length === 0 ? (
              <p className="text-sm text-gray-500">
                No sites available for this client.
              </p>
            ) : (
              <>
                <p className="mb-2 text-sm text-gray-500">
                  Choose which sites this user can view and post jobs to.
                </p>
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="text-gray-500">
                    {selectedSiteIds.length} of {clientSites.length} selected
                  </span>
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="text"
                      onClick={selectAllSites}
                      className="px-0 py-0 font-medium"
                    >
                      Select all
                    </Button>
                    <Button
                      type="button"
                      variant="text"
                      onClick={clearSites}
                      className="px-0 py-0 font-medium"
                    >
                      Clear
                    </Button>
                  </div>
                </div>

                <div className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
                  {clientSites.map((site) => (
                    <label
                      key={site.id}
                      className="flex cursor-pointer items-start gap-3 px-4 py-3 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSiteIds.includes(site.id)}
                        onChange={() => toggleSite(site.id)}
                        className="mt-0.5 h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="flex flex-col">
                        <span className="flex items-center gap-2">
                          <span className="text-sm text-gray-900">
                            {site.name}
                          </span>
                          {site.status === SiteStatus.Inactive && (
                            <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-red-700 uppercase">
                              Disabled
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-gray-500">
                          {generateFriendlyAddress(site.address ?? {}) || '-'}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-3 border-t pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium"
            >
              {isSaving ? 'Saving...' : 'Done'}
            </Button>
          </div>
        </div>
      ) : (
        <dl className="divide-y divide-gray-100">
          <div className="flex items-start justify-between px-6 py-4">
            <dt className="text-sm text-gray-500">Name</dt>
            <dd className="text-right text-sm font-medium text-gray-900">
              {name || '-'}
            </dd>
          </div>
          <div className="flex items-start justify-between px-6 py-4">
            <dt className="text-sm text-gray-500">Email Address</dt>
            <dd className="max-w-[60%] text-right text-sm font-medium text-gray-900">
              {email || '-'}
            </dd>
          </div>
          <div className="flex items-start justify-between px-6 py-4">
            <dt className="text-sm text-gray-500">Role</dt>
            <dd className="text-right text-sm font-medium text-gray-900">
              {role}
            </dd>
          </div>
          <div className="flex items-start justify-between px-6 py-4">
            <dt className="text-sm text-gray-500">Sites</dt>
            <dd className="max-w-[60%] text-right text-sm font-medium text-gray-900">
              {accessibleSites.length === 0 ? (
                '-'
              ) : (
                <ul className="space-y-1">
                  {accessibleSites.map((site) => (
                    <li
                      key={site.id}
                      className="flex items-center justify-end gap-2"
                    >
                      {site.name}
                      {site.status === SiteStatus.Inactive && (
                        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-red-700 uppercase">
                          Disabled
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
};

export default ClientRolesAndPermissionsDetails;
